import React, { FC } from 'react'
import { Trans, t } from '@lingui/macro'
import { useQueryParam, NumberParam, withDefault } from 'use-query-params'
import { linkBuilder } from 'shared/linkBuilder'
import { Page } from '../../components/common/page/Page'
import { Box, Flex } from '@chakra-ui/react'
import PageHeader from '../../components/common/page/header/PageHeader'
import CardGrid from '../../components/common/card-grid/CardGrid'
import PathwayCard from './components/PathwayCard'
import Tag from '../../components/common/tag/Tag'
import Loader from '../../components/common/loader/Loader'
import ListEmpty from '../../components/common/list/ListEmpty'
import HeadTitle from '../../components/common/head/HeadTitle'
import Pagination from '../../components/common/pagination/Pagination'
import {
  useTagsForPathwaysQuery,
  usePathwayStudentViewQuery
} from './StudentPathwaysPage.codegen'

import './StudentPathwaysPage.less'
import { StudentPathwayTopbar } from './components/StudentPathwayTopbar'
import { includes } from 'lodash'
import { adminAndManagerOrganisationRoles } from 'types/Organisation'
import { usePublicApiSession } from '../../public-api/hooks/usePublicApiSession'
import { brandColors } from 'shared/basicPalette'
import { css } from '@emotion/react'
import { PathwaysListEnrolmentsFilter } from 'front-end/src/graphql/private/PrivateSchemaTypes'

const PAGE_SIZE_LIMIT = 20

const StudentPathwaysPageTagsList = (props) => {
  const [tagId] = useQueryParam('tagId', NumberParam)

  const { data, loading } = useTagsForPathwaysQuery()

  if (loading) {
    return <Loader />
  }

  if (data?.tags && data?.tags.length > 0) {
    const tags = [{ name: t`All`, id: null }, ...data.tags]

    const getTagColor = (tag) => {
      if (!tagId && tag.id === null) {
        return 'primary'
      } else {
        return tagId === tag.id ? 'primary' : 'clear'
      }
    }

    return (
      <Box className="StudentPathwaysPage__TagsList">
        {tags.map((tag, i) => {
          return (
            <Tag
              key={i}
              color={getTagColor(tag)}
              size="normal"
              onClick={() => props.onTagClick && props.onTagClick(tag)}
            >
              {tag.name}
            </Tag>
          )
        })}
      </Box>
    )
  } else {
    return null
  }
}

export const StudentPathwaysPage: FC = () => {
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 1))

  const [tagId, setTagId] = useQueryParam('tagId', NumberParam)

  const { data: pathwaysData, loading: pathwaysLoading } =
    usePathwayStudentViewQuery({
      variables: {
        tagId,
        limit: PAGE_SIZE_LIMIT,
        page: page - 1 || 0,
        enrolmentsFilter: PathwaysListEnrolmentsFilter.WITHOUT_ENROLMENTS_ONLY
      }
    })

  const {
    data: pathwaysWithEnrolmentsData,
    loading: pathwaysWithEnrolmentsLoading
  } = usePathwayStudentViewQuery({
    variables: {
      enrolmentsFilter: PathwaysListEnrolmentsFilter.WITH_ENROLMENTS_ONLY
    },
    fetchPolicy: 'network-only'
  })

  const { session } = usePublicApiSession()

  if (
    pathwaysLoading ||
    !pathwaysData ||
    pathwaysWithEnrolmentsLoading ||
    !pathwaysWithEnrolmentsData
  ) {
    return <Loader />
  }

  const {
    pathways: { count: totalCount }
  } = pathwaysData

  const isAdminOrManager = includes(
    adminAndManagerOrganisationRoles,
    session?.user.organisation_role
  )

  const pathwaysWithEnrolments = pathwaysWithEnrolmentsData.pathways.items || []
  const pathwaysWithOutEnrolments = pathwaysData.pathways.items || []

  const renderPathwayCards = (pathways) => {
    return (
      <CardGrid
        cards={pathways.map((pathway) => {
          return (
            <PathwayCard
              key={pathway.id}
              linkUrl={linkBuilder.pathwayDetail(pathway.id)}
              pathway={pathway}
              progress={
                pathway.currentEnrolment && pathway.currentEnrolment.progress
              }
            />
          )
        })}
      />
    )
  }

  const onPageChange = (page) => setPage(page)

  const renderPagination = (totalCount) => {
    const totalPages = Math.ceil(totalCount / PAGE_SIZE_LIMIT)

    if (totalPages <= 1) {
      return null
    }

    return (
      <Flex padding="16px" alignItems="center" justifyContent="space-between">
        <Box flex="1" width="15%" color={brandColors.grey.mid}>
          <Trans>{totalCount} found</Trans>
        </Box>
        <Pagination
          totalPages={totalPages}
          page={page}
          onPageChange={onPageChange}
          css={css`
            img {
              width: 70%;
              text-align: center;
              flex: 1;
            }
          `}
        />
        <Box flex="1" />
      </Flex>
    )
  }

  return (
    <>
      {isAdminOrManager && <StudentPathwayTopbar />}
      <Page className="StudentPathwaysPage" withTopbar={isAdminOrManager}>
        <HeadTitle title={t`Pathways`} />
        <Box className="StudentPathwaysPage__YourPathways">
          <PageHeader
            className="PageHeader"
            title={t`Your pathways`}
            showBackButton={false}
          />
          {pathwaysWithEnrolments.length > 0 ? (
            <Box className="StudentPathwaysPage__inner">
              {renderPathwayCards(pathwaysWithEnrolments)}
            </Box>
          ) : (
            <ListEmpty
              title={t`You don't have any active pathways`}
              description={
                pathwaysWithOutEnrolments.length > 0
                  ? t`Choose a new pathway to get started`
                  : t`Pathways you can explore will be displayed below`
              }
            />
          )}
        </Box>
        <Box className="StudentPathwaysPage__Explore">
          <Box className="StudentPathwaysPage__inner">
            <h2 className="StudentPathwaysPage__Explore__header">
              <Trans>Explore more pathways</Trans>
            </h2>
            <StudentPathwaysPageTagsList
              pathways={pathwaysWithOutEnrolments}
              onTagClick={(tag) => {
                setTagId(tag.id)
                setPage(1)
              }}
            />
            {pathwaysWithOutEnrolments.length > 0 ? (
              renderPathwayCards(
                tagId
                  ? pathwaysWithOutEnrolments.filter((pathway) => {
                      return pathway.tags.find((t) => Number(tagId) === t.id)
                    })
                  : pathwaysWithOutEnrolments
              )
            ) : tagId ? (
              <ListEmpty
                showImage={false}
                description={t`You don't have access to any pathways with this tag`}
              />
            ) : (
              <ListEmpty
                showImage={false}
                description={t`You don't have any pathways available right now`}
              />
            )}
            <Box>{renderPagination(totalCount)}</Box>
          </Box>
        </Box>
      </Page>
    </>
  )
}
